import { useMemo } from 'react'

import { Rule } from 'antd/lib/form'

import { Input, InputProps } from '../shared-components/BUI/Input'

import { useLocale, Locale } from '@/context/localeContext'
import { prettyAbn, prettyCrn } from '@/utils/index'

const sanitiseAbnCrn = (value: string | number, length: number) => {
  return value?.toString().replace(/[^\d]/g, '').slice(0, length)
}

/**
 * Checks ABN for validity using the published ABN checksum algorithm.
 * @author Guy Carpenter
 * @license http://www.clearwater.com.au/code None
 * @param  {String|Number} value abn to validate
 * @return {Boolean} Is ABN Valid
 */
const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19]

const validateAbn =
  (t: ReturnType<typeof useLocale>['t']) => (value: number | string) => {
    const abn = sanitiseAbnCrn(value, 11)

    if (abn?.length === 11) {
      let sum = 0

      for (let index = 0; index <= ABN_WEIGHTS.length - 1; index++) {
        const weight = ABN_WEIGHTS[index]
        const digit = Number(abn[index]) - (index > 0 ? 0 : 1) // subtract 1 from first digit only
        sum += weight * digit
      }

      if (sum % 89 === 0) {
        return
      }
    } else {
      return t('abn:errors.length', { length: 11 })
    }

    return t('abn:errors.invalid')
  }

const validateCrn =
  (t: ReturnType<typeof useLocale>['t']) => (value: string | number) => {
    const crn = sanitiseAbnCrn(value, 8)
    if (crn?.length === 8) {
      return
    }

    return t('abn:errors.length', { length: 8 })
  }

type BusinessNumberInputConfig = {
  placeholder: string
  formatter: (value: string) => string | undefined
  sanitiser: (value: string | number) => string
  validator: (value: string | number) => string | undefined
}

const LOCALE_TO_COMPANY_INPUT_CONFIG = (
  t: ReturnType<typeof useLocale>['t']
): Record<Locale, BusinessNumberInputConfig> => ({
  [Locale.enAU]: {
    placeholder: '12 345 678 901',
    formatter: prettyAbn,
    sanitiser: (value: string) => sanitiseAbnCrn(value, 11),
    validator: validateAbn(t),
  },
  [Locale.enGB]: {
    placeholder: '12345678',
    formatter: prettyCrn,
    sanitiser: (value: string) => sanitiseAbnCrn(value, 8),
    validator: validateCrn(t),
  },
  [Locale.enUS]: {
    placeholder: t('abn:placeholders.businessNumberInput'),
    formatter: (value: string | number) => value?.toString(),
    sanitiser: (value: string | number) => value?.toString(),
    validator: () => undefined,
  },
  [Locale.enCA]: {
    placeholder: t('abn:placeholders.businessNumberInput'),
    formatter: (value: string | number) => value?.toString(),
    sanitiser: (value: string | number) => value?.toString(),
    validator: () => undefined,
  },
  [Locale.esAU]: {
    placeholder: '12 345 678 901',
    formatter: (value: string | number) => value?.toString(),
    sanitiser: (value: string | number) => value?.toString(),
    validator: () => undefined,
  },
  [Locale.esGB]: {
    placeholder: '12345678',
    formatter: (value: string | number) => value?.toString(),
    sanitiser: (value: string | number) => value?.toString(),
    validator: () => undefined,
  },
  [Locale.esUS]: {
    placeholder: t('abn:placeholders.businessNumberInput'),
    formatter: (value: string | number) => value?.toString(),
    sanitiser: (value: string | number) => value?.toString(),
    validator: () => undefined,
  },
  [Locale.esCA]: {
    placeholder: t('abn:placeholders.businessNumberInput'),
    formatter: (value: string | number) => value?.toString(),
    sanitiser: (value: string | number) => value?.toString(),
    validator: () => undefined,
  },
})

export const useBusinessNumberInput = () => {
  const { t, locale } = useLocale()

  const config = LOCALE_TO_COMPANY_INPUT_CONFIG(t)[locale]
  const validateBusinessNumber = config.validator

  const validationRules: Rule[] = useMemo(
    () => [
      {
        validator: (_, value) => {
          if (value === undefined || value === null || value === '') {
            return Promise.resolve()
          }

          const validationErrors = validateBusinessNumber(value)

          if (validationErrors?.length > 0) {
            return Promise.reject(validationErrors)
          }

          return Promise.resolve()
        },
      },
    ],
    [validateBusinessNumber]
  )

  return {
    validationRules,
    validateBusinessNumber,
    formatBusinessNumber: config.formatter,
    sanitiseBusinessNumber: config.sanitiser,
    placeholder: config.placeholder,
  }
}

type BusinessNumberInput = InputProps & {
  value?: string
  onChange?: (value: string) => void
}

export const BusinessNumberInput: React.FC<BusinessNumberInput> = ({
  value,
  onChange,
  ...inputProps
}) => {
  const { formatBusinessNumber, sanitiseBusinessNumber, placeholder } =
    useBusinessNumberInput()

  return (
    <Input
      style={{ width: '100%' }}
      placeholder={placeholder}
      {...inputProps}
      value={formatBusinessNumber(value || '')}
      onChange={(e) =>
        onChange?.(sanitiseBusinessNumber(e.target.value?.toString() || ''))
      }
    />
  )
}
